import { PostHogEvents } from '@/utils/PostHogEvents';
import { trackEvent } from '@/utils/track-event';
import { useEffect, useRef, useState, ReactNode } from 'react';

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  className?: string;
  width?: string; // Optional width prop for future use
}

const Tooltip = ({
  children,
  content,
  position = 'bottom',
  delay = 300,
  className = 'bg-gray-800 text-white',
  width = '250px', // Default width, can be overridden
}: TooltipProps) => {
  const [active, setActive] = useState(false);
  const [hoverTimer, setHoverTimer] = useState<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Get arrow class based on position
  const getArrowClass = () => {
    const classes = {
      top: 'after:top-full after:left-1/2 after:-ml-1 after:border-t-gray-800',
      bottom: 'after:bottom-full after:left-1/2 after:-ml-1 after:border-b-gray-800',
      left: 'after:left-full after:top-1/2 after:-mt-1 after:border-l-gray-800',
      right: 'after:right-full after:top-1/2 after:-mt-1 after:border-r-gray-800',
    };

    return classes[position] || classes.bottom;
  };

  // Show tooltip with delay
  const showTooltip = () => {
    clearTimeout(hoverTimer);
    const timer = setTimeout(() => {
      setActive(true);
      // Track tooltip opened event
      trackEvent(PostHogEvents.TOOLTIP_OPENED, {
        tooltip_id: typeof content === 'string' ? content : 'unknown',
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
      });
    }, delay);
    setHoverTimer(timer);
  };

  // Hide tooltip
  const hideTooltip = () => {
    clearTimeout(hoverTimer);
    setActive(false);
  };

  // Clean up timer
  useEffect(() => {
    return () => {
      if (hoverTimer) clearTimeout(hoverTimer);
    };
  }, [hoverTimer]);

  // Position the tooltip when it becomes active
  useEffect(() => {
    if (active && tooltipRef.current && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();

      // Calculate position based on the viewport coordinates, not relative positioning
      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = triggerRect.top - (tooltipRef.current.offsetHeight + 8);
          left = triggerRect.left + (triggerRect.width - tooltipRef.current.offsetWidth) / 2;
          break;
        case 'bottom':
          top = triggerRect.bottom + 8;
          left = triggerRect.left + (triggerRect.width - tooltipRef.current.offsetWidth) / 2;
          break;
        case 'left':
          top = triggerRect.top + (triggerRect.height - tooltipRef.current.offsetHeight) / 2;
          left = triggerRect.left - (tooltipRef.current.offsetWidth + 8);
          break;
        case 'right':
          top = triggerRect.top + (triggerRect.height - tooltipRef.current.offsetHeight) / 2;
          left = triggerRect.right + 8;
          break;
        default:
          top = triggerRect.bottom + 8;
          left = triggerRect.left + (triggerRect.width - tooltipRef.current.offsetWidth) / 2;
      }

      // Apply bounds checking to prevent tooltip from going off-screen
      if (left < 10) left = 10;
      if (left + (tooltipRef.current?.offsetWidth || 0) > window.innerWidth - 10) {
        left = window.innerWidth - (tooltipRef.current?.offsetWidth || 0) - 10;
      }

      if (top < 10) top = 10;
      if (top + (tooltipRef.current?.offsetHeight || 0) > window.innerHeight - 10) {
        top = window.innerHeight - (tooltipRef.current?.offsetHeight || 0) - 10;
      }

      // Apply the calculated position
      tooltipRef.current.style.top = `${top}px`;
      tooltipRef.current.style.left = `${left}px`;
    }
  }, [active, position]);

  const arrowClass = getArrowClass();

  return (
    <div
      className="relative inline-block"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      ref={triggerRef}
    >
      {children}

      {active && (
        <div
          ref={tooltipRef}
          className={`fixed z-50 -translate-y-1 px-2 py-2 text-xs rounded shadow-lg ${className} ${arrowClass} after:absolute after:border-4 after:border-transparent w-[${width}]`}
          role="tooltip"
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default Tooltip;
